import React from "react";
import {Box, IconButton, Tooltip} from "@mui/material";
import {Close, DescriptionOutlined, FilterAltOutlined, QueryStatsOutlined} from "@mui/icons-material";
import type {GridColDef, GridRowId, useGridApiRef} from "@mui/x-data-grid-premium";
import {DataGridPremium, GRID_DETAIL_PANEL_TOGGLE_FIELD} from "@mui/x-data-grid-premium";
import type {ContractChild, ContractParent} from "../contractCoverageData";
import {COVERAGE_CURRENCY} from "../contractCoverageData";
import type {CoverageFilterKey} from "../contractCoverageUtils";
import {
    actionForPosition,
    childMatchesFilter,
    FILTER_LABEL,
    formatCompactCurrency,
    formatNumber,
    formatPercent,
    POSITION_LABEL,
    POSITION_ORDER,
    positionColors,
    utilizationColor,
} from "../contractCoverageUtils";
import {OverviewDrawer} from "~/experiments/pages/contract-coverage-intelligence/components/OverviewDrawer.tsx";
import {ScoreViewDrawer} from "./ScoreViewDrawer.tsx";

export type CoverageGridApiRef = ReturnType<typeof useGridApiRef>;

type Props = {
    rows: ContractParent[];
    apiRef: CoverageGridApiRef;
    activeFilter: CoverageFilterKey | null;
    onClearFilter: () => void;
    loading?: boolean;
};

/**
 * Zebra variant of the coverage table. Differences from the standard table:
 * - No child selection, no execution plan.
 * - The contract analysis overview lives at parent level: parent names are links that open
 *   the overview drawer directly (no "Generate overview" CTA).
 * - Scoring is read-only (see ScoreViewDrawer).
 */
export const ZebraCoverageTable: React.FC<Props> = ({rows, apiRef, activeFilter, onClearFilter, loading = false}) => {
    // Controlled so opening drawers doesn't collapse the open panel on re-render/re-sort.
    const [expandedIds, setExpandedIds] = React.useState<Set<GridRowId>>(new Set());
    const [overviewId, setOverviewId] = React.useState<string | null>(null);
    const [scoreChild, setScoreChild] = React.useState<ContractChild | null>(null);

    // Filter: keep parents with at least one matching child.
    const filteredRows = React.useMemo(
        () => activeFilter
            ? rows.filter(p => p.children.some(ch => childMatchesFilter(ch, activeFilter)))
            : rows,
        [rows, activeFilter]);

    const matchCount = React.useMemo(
        () => activeFilter
            ? filteredRows.reduce((s, p) => s + p.children.filter(ch => childMatchesFilter(ch, activeFilter)).length, 0)
            : 0,
        [filteredRows, activeFilter]);

    // Auto-expand matching groups when a filter is applied; collapse when cleared.
    const [prevFilter, setPrevFilter] = React.useState(activeFilter);
    if (prevFilter !== activeFilter) {
        setPrevFilter(activeFilter);
        setExpandedIds(activeFilter ? new Set(filteredRows.map(p => p.id)) : new Set());
    }

    const toggleExpanded = React.useCallback((id: GridRowId) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const openOverview = React.useCallback((parentId: string) => setOverviewId(parentId), []);

    const columns = React.useMemo<GridColDef<ContractParent>[]>(() => [
        {
            field: 'name',
            headerName: 'Parent supplier',
            flex: 2.4,
            minWidth: 300,
            renderCell: ({row}) => (
                <div className="cci-supplier-cell">
                    <Tooltip title="Open contract analysis overview" arrow placement="top-start">
                        <button
                            type="button"
                            className="ccz-parent-link"
                            onClick={e => {
                                e.stopPropagation();
                                openOverview(row.id);
                            }}>
                            <span className="cci-supplier-name">{row.name}</span>
                            <DescriptionOutlined className="ccz-parent-link-icon" fontSize="inherit"/>
                        </button>
                    </Tooltip>
                    <div className="cci-supplier-meta">
                        {row.country && <span className="cci-country-chip">{row.country}</span>}
                        <span>{formatNumber(row.childCount)} supplier{row.childCount === 1 ? '' : 's'}</span>
                        {row.contractedChildren > 0 &&
                            <span>· {formatNumber(row.contractedChildren)} contracted</span>}
                    </div>
                </div>
            ),
        },
        {
            field: 'spend',
            headerName: 'Cumulative spend',
            type: 'number',
            flex: 1,
            minWidth: 150,
            renderCell: ({row}) => (
                <span className="cci-num cci-spend">{formatCompactCurrency(row.spend, COVERAGE_CURRENCY)}</span>
            ),
        },
        {
            field: 'costSavingEur',
            headerName: 'Cost saving',
            type: 'number',
            flex: 1,
            minWidth: 140,
            renderCell: ({row}) =>
                row.costSavingEur > 0
                    ? <span className="cci-num cci-saving">{formatCompactCurrency(row.costSavingEur, COVERAGE_CURRENCY)}</span>
                    : <span className="cci-num cci-muted">—</span>,
        },
        {
            field: 'costAvoidanceEur',
            headerName: 'Cost avoidance',
            type: 'number',
            flex: 1,
            minWidth: 140,
            renderCell: ({row}) =>
                row.costAvoidanceEur > 0
                    ? <span className="cci-num cci-avoidance">{formatCompactCurrency(row.costAvoidanceEur, COVERAGE_CURRENCY)}</span>
                    : <span className="cci-num cci-muted">—</span>,
        },
    ], [openOverview]);

    return (
        <Box sx={{width: '100%'}}>
            {activeFilter && (
                <div className="cci-filterbar">
                    <span className="cci-filter-chip">
                        <FilterAltOutlined fontSize="inherit"/>
                        {FILTER_LABEL[activeFilter]}
                        <span className="cci-filter-count">
                            {formatNumber(matchCount)} supplier{matchCount === 1 ? '' : 's'} · {formatNumber(filteredRows.length)} group{filteredRows.length === 1 ? '' : 's'}
                        </span>
                        <button type="button" className="cci-filter-clear" onClick={onClearFilter} aria-label="Clear filter">
                            <Close fontSize="inherit"/>
                        </button>
                    </span>
                </div>
            )}
            <DataGridPremium
                className="base-table-muix auto-row-height-centered"
                apiRef={apiRef}
                rows={filteredRows}
                columns={columns}
                loading={loading}
                getRowId={row => row.id}
                getRowHeight={() => 'auto'}
                onRowClick={({id}, event) => {
                    // The detail toggle chevron already toggles the panel — don't double-toggle.
                    const el = event.target as HTMLElement;
                    if (el.closest(`[data-field="${GRID_DETAIL_PANEL_TOGGLE_FIELD}"]`)) return;
                    toggleExpanded(id);
                }}
                getDetailPanelContent={({row}) => (
                    <ZebraDetailPanel
                        parent={row}
                        filter={activeFilter}
                        onOpenOverview={() => openOverview(row.id)}
                        onViewScore={setScoreChild}
                    />
                )}
                getDetailPanelHeight={() => 'auto'}
                detailPanelExpandedRowIds={expandedIds}
                onDetailPanelExpandedRowIdsChange={setExpandedIds}
                headerFilters
                disableRowSelectionOnClick
                disableAggregation
                disableRowGrouping
                pagination
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{
                    pagination: {paginationModel: {pageSize: 25, page: 0}},
                    sorting: {sortModel: [{field: 'costSavingEur', sort: 'desc'}]},
                }}
                sx={{
                    border: 'none',
                    '--rowBorderColor': 'rgba(0,0,0,0.08)',
                    '& .MuiDataGrid-columnHeaderTitle': {fontWeight: 700},
                    '& .MuiDataGrid-row': {cursor: 'pointer'},
                }}
            />

            <OverviewDrawer
                open={overviewId !== null}
                onClose={() => setOverviewId(null)}
                parentId={overviewId}
            />

            <ScoreViewDrawer
                open={scoreChild !== null}
                onClose={() => setScoreChild(null)}
                child={scoreChild}
            />
        </Box>
    );
};

type DetailProps = {
    parent: ContractParent;
    filter: CoverageFilterKey | null;
    onOpenOverview: () => void;
    onViewScore: (child: ContractChild) => void;
};

const ZebraDetailPanel: React.FC<DetailProps> = ({parent, filter, onOpenOverview, onViewScore}) => {
    // When a filter is active, only matching children are shown.
    const display = React.useMemo(
        () => parent.children
            .filter(ch => !filter || childMatchesFilter(ch, filter))
            .slice()
            .sort((a, b) => POSITION_ORDER[a.position] - POSITION_ORDER[b.position] || b.spend - a.spend),
        [parent.children, filter]);

    return (
        <div className="cci-detail">
            <div className="cci-detail-facts">
                <Fact label="Suppliers" value={formatNumber(parent.childCount)}/>
                <Fact label="Contracted suppliers" value={formatNumber(parent.contractedChildren)}/>
                <Fact label="Cumulative spend" value={formatCompactCurrency(parent.spend, COVERAGE_CURRENCY)}/>
                <Fact label="Cost saving" value={parent.costSavingEur > 0 ? formatCompactCurrency(parent.costSavingEur, COVERAGE_CURRENCY) : '—'} tone="saving"/>
                <Fact label="Cost avoidance" value={parent.costAvoidanceEur > 0 ? formatCompactCurrency(parent.costAvoidanceEur, COVERAGE_CURRENCY) : '—'} tone="avoidance"/>
                {parent.contractedChildren > 0 && <button
                    type="button"
                    className="ccz-overview-cta"
                    onClick={e => {
                                e.stopPropagation();
                                onOpenOverview();
                            }}
                >
                    <DescriptionOutlined fontSize="small"/>
                    View contract overview
                </button>}
            </div>

            <div className="cci-children-scroll">
                <table className="cci-children">
                    <thead>
                        <tr>
                            <th>Supplier</th>
                            <th>Contract position</th>
                            <th className="cci-children-num">Spend</th>
                            <th className="cci-children-num">Transactions</th>
                            <th className="cci-children-num">Entities</th>
                            <th className="cci-children-num">Active months</th>
                            <th className="cci-children-num">Contracted value</th>
                            <th className="cci-children-num">Utilisation</th>
                            <th className="cci-children-num">Cost saving</th>
                            <th className="cci-children-num">Cost avoidance</th>
                            <th className="cci-children-action">Scoring</th>
                        </tr>
                    </thead>
                    <tbody>
                        {display.map((ch, i) => (
                            <ChildRow key={i} ch={ch} onView={() => onViewScore(ch)}/>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const ChildRow: React.FC<{
    ch: ContractChild;
    onView: () => void;
}> = ({ch, onView}) => {
    const c = positionColors(ch.position);
    const hasValue = ch.contractedValueEur > 0;
    return (
        <tr title={actionForPosition(ch.position)}>
            <td>
                {ch.supplier}
                {ch.country && <span className="cci-country-chip cci-inline">{ch.country}</span>}
            </td>
            <td>
                <span className="cci-position-pill" style={{backgroundColor: c.bg, color: c.fg}}>
                    {POSITION_LABEL[ch.position]}
                </span>
            </td>
            <td className="cci-children-num">{formatCompactCurrency(ch.spend, COVERAGE_CURRENCY)}</td>
            <td className="cci-children-num">{formatNumber(ch.transactions)}</td>
            <td className="cci-children-num">{formatNumber(ch.entities)}</td>
            <td className="cci-children-num">{formatNumber(ch.activeMonths)}</td>
            <td className="cci-children-num">
                {hasValue ? formatCompactCurrency(ch.contractedValueEur, COVERAGE_CURRENCY) : '—'}
            </td>
            <td className="cci-children-num">
                {hasValue
                    ? <span style={{color: utilizationColor(ch.utilizationPct)}}>{formatPercent(ch.utilizationPct)}</span>
                    : '—'}
            </td>
            <td className="cci-children-num">
                {ch.costSavingEur > 0
                    ? <span className="cci-saving">{formatCompactCurrency(ch.costSavingEur, COVERAGE_CURRENCY)}</span>
                    : '—'}
            </td>
            <td className="cci-children-num">
                {ch.costAvoidanceEur > 0
                    ? <span className="cci-avoidance">{formatCompactCurrency(ch.costAvoidanceEur, COVERAGE_CURRENCY)}</span>
                    : '—'}
            </td>
            <td className="cci-children-action">
                <Tooltip title="View scoring" arrow>
                    <IconButton size="small" onClick={onView}>
                        <QueryStatsOutlined fontSize="small"/>
                    </IconButton>
                </Tooltip>
            </td>
        </tr>
    );
};

const Fact: React.FC<{label: string; value: string; tone?: 'saving' | 'avoidance'}> = ({label, value, tone}) => (
    <div className="cci-fact">
        <span className="cci-fact-label">{label}</span>
        <span className={`cci-fact-value${tone ? ` is-${tone}` : ''}`}>{value}</span>
    </div>
);
